import { el } from "../lib/dom.ts";
import { buildRoute } from "../lib/route.ts";
import { listTrails } from "../lib/store.ts";
import { createDrawer } from "./drawer.ts";

interface LegacySavedPathData {
  anchorRef: string;
  anchorLabel: string;
  nodes: { ref: string; label: string }[];
  savedAt: number;
}

interface SavedSessionTrailEntry {
  ref: string;
  label: string;
  anchorRef: string;
  anchorLabel: string;
  path: string[];
}

interface SessionTrailSaveData {
  kind: "session-trail-v2";
  parashaRef: string;
  entries: SavedSessionTrailEntry[];
  savedAt: number;
}

interface SavedPathRow {
  parasha: string;
  title: string;
  steps: number;
  savedAt: number;
  path: string[];
}

const drawer = createDrawer("saved-paths-drawer", "Saved Trails");

function isLegacySavedPathData(d: unknown): d is LegacySavedPathData {
  if (typeof d !== "object" || d === null) return false;
  const o = d as Record<string, unknown>;
  if (typeof o.anchorRef !== "string" || typeof o.anchorLabel !== "string" || typeof o.savedAt !== "number") return false;
  if (!Array.isArray(o.nodes)) return false;
  return o.nodes.every((n) => {
    if (typeof n !== "object" || n === null) return false;
    const m = n as Record<string, unknown>;
    return typeof m.ref === "string" && typeof m.label === "string";
  });
}

function isSessionTrailSaveData(d: unknown): d is SessionTrailSaveData {
  if (typeof d !== "object" || d === null) return false;
  const o = d as Record<string, unknown>;
  if (o.kind !== "session-trail-v2" || typeof o.parashaRef !== "string" || typeof o.savedAt !== "number") return false;
  if (!Array.isArray(o.entries) || !o.entries.length) return false;
  return o.entries.every((entry) => {
    if (typeof entry !== "object" || entry === null) return false;
    const e = entry as Record<string, unknown>;
    return (
      typeof e.ref === "string" &&
      typeof e.label === "string" &&
      typeof e.anchorRef === "string" &&
      typeof e.anchorLabel === "string" &&
      Array.isArray(e.path) &&
      e.path.every((p) => typeof p === "string")
    );
  });
}

function normalizeTrail(name: string, data: unknown): SavedPathRow | null {
  if (!name.startsWith("journey:")) return null;
  const legacyParasha = name.slice("journey:".length);
  if (isSessionTrailSaveData(data)) {
    const first = data.entries[0];
    const last = data.entries[data.entries.length - 1];
    const path = last.path.length ? last.path : [last.anchorRef, last.ref];
    return {
      parasha: data.parashaRef || legacyParasha,
      title: `${first.anchorLabel} -> ${last.label}`,
      steps: data.entries.length,
      savedAt: data.savedAt,
      path,
    };
  }
  if (isLegacySavedPathData(data)) {
    const last = data.nodes[data.nodes.length - 1];
    return {
      parasha: legacyParasha,
      title: last ? `${data.anchorLabel} -> ${last.label}` : data.anchorLabel,
      steps: data.nodes.length,
      savedAt: data.savedAt,
      path: [data.anchorRef, ...data.nodes.map((n) => n.ref)],
    };
  }
  return null;
}

function restoreSavedPathFocus(host: HTMLElement): void {
  const next = host.querySelector<HTMLButtonElement>("button") ?? host.closest(".saved-paths-drawer")?.querySelector<HTMLButtonElement>(".drawer-close");
  next?.focus();
}

async function fillSavedPaths(host: HTMLElement): Promise<void> {
  const hadFocus = document.activeElement instanceof HTMLElement && host.contains(document.activeElement);
  const trails = await listTrails();
  const rows = trails
    .map((t) => normalizeTrail(t.name, t.data))
    .filter((r): r is SavedPathRow => !!r)
    .sort((a, b) => b.savedAt - a.savedAt);

  host.replaceChildren();
  if (!rows.length) {
    const empty = el("div", "study-empty");
    empty.append(el("div", "study-empty-title", "No saved trails yet"));
    empty.append(el("div", "study-empty-copy", "Save a trail when you want to continue it later."));
    host.append(empty);
    if (hadFocus) restoreSavedPathFocus(host);
    return;
  }

  const list = el("ul", "jtrails-list saved-path-list");
  list.setAttribute("role", "list");
  for (const row of rows) {
    const li = el("li", "jtrail");
    li.setAttribute("role", "listitem");
    const b = el("button", "jtrail-row saved-path-row");
    b.type = "button";
    b.setAttribute("aria-label", `Resume saved trail: ${row.title}`);
    const txt = el("span", "jmap-txt");
    txt.append(el("span", "jmap-label", row.title));
    const when = new Date(row.savedAt).toLocaleDateString();
    txt.append(el("span", "jtrail-meta", `${row.steps} ${row.steps === 1 ? "step" : "steps"} - saved ${when}`));
    b.append(el("span", "jmap-mark jmap-mark-path"), txt);
    b.addEventListener("click", () => {
      closeSavedPathsDrawer(false);
      location.hash = buildRoute(row.parasha, row.path);
    });
    li.append(b);
    list.append(li);
  }
  host.append(list);
  if (hadFocus) restoreSavedPathFocus(host);
}

export function buildSavedPathsPanel(): HTMLElement {
  const panel = el("div", "bookmarks-panel saved-paths-panel");
  const head = el("div", "bookmarks-head");
  head.append(el("div", "bookmarks-title", "Saved Trails"));
  head.append(el("div", "bookmarks-sub", "Session trails you saved for later."));
  const body = el("div", "bookmarks-body");
  body.append(el("div", "journey-empty", "Loading saved trails..."));
  panel.append(head, body);
  void fillSavedPaths(body);
  return panel;
}

export function openSavedPathsDrawer(): void {
  drawer.open(() => buildSavedPathsPanel());
}

export function refreshSavedPathsDrawer(): void {
  drawer.refresh();
}

export function closeSavedPathsDrawer(restoreFocus = true): void {
  drawer.close(restoreFocus);
}

export function _resetSavedPathsViewForTests(): void {
  drawer.close(false);
}
